'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { VisuallyHidden } from '@/components/ui/visually-hidden';
import { cn } from '@/lib/utils';
import { Command, CommandInput, CommandList } from '@/components/ui/command';
import { MATTER_ROUTES } from '@/lib/constants';
import { SearchResultsView } from './search-results-view';
import { RecentMattersView } from './recent-matters-view';
import { QuickActionsModalProps } from './types';
import { MatterSearchResult } from '@/components/search/hooks/use-search';
import { recordRecentMatterView } from '@/lib/hooks/use-recent-matters';
import type { GlobalContact } from '@/lib/services/contacts';

/**
 * QuickActionsModal Component
 *
 * Main modal that orchestrates two views:
 * - 'list': Shows recent matters (blank/initial state)
 * - 'search': Shows search results (when user types)
 */
export function QuickActionsModal({
  open,
  onOpenChange
}: QuickActionsModalProps) {
  const router = useRouter();

  const [searchQuery, setSearchQuery] = useState('');

  // Reset modal state during render whenever it transitions closed → re-opened
  // closed, using the prev-renders pattern.
  const [prevOpen, setPrevOpen] = useState(open);
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (!open) {
      setSearchQuery('');
    }
  }

  const isSearching = searchQuery.length >= 2;

  // Handle keyboard events - capture Escape key before Command component
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        onOpenChange(false);
      }
    };

    // Use capture phase and add to window for highest priority
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () =>
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [open, onOpenChange]);

  // Handle search result selection
  const handleSearchResultSelect = (matter: MatterSearchResult) => {
    recordRecentMatterView(matter.id);
    onOpenChange(false);
    // All matters navigate to the details page
    router.push(MATTER_ROUTES.OVERVIEW(matter.id));
  };

  // Handle contact search result selection
  const handleContactSelect = (contact: GlobalContact) => {
    onOpenChange(false);
    router.push(`/contacts?contact=${contact.id}`);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} modal>
      <DialogPrimitive.Portal>
        {/* Transparent overlay */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-transparent" />

        {/* Modal content with custom styling */}
        <DialogPrimitive.Content
          className={cn(
            'fixed top-[50%] left-[50%] z-50 w-full max-w-3xl translate-x-[-50%] translate-y-[-50%]',
            'overflow-hidden rounded-xl p-0',
            'bg-[linear-gradient(0deg,#FAFAFA_0%,#FAFAFA_100%),#FFF]',
            'shadow-[0_8px_28px_-6px_rgba(31,28,29,0.12),0_18px_88px_-4px_rgba(31,28,29,0.16)]',
            // Fixed, viewport-clamped height so the box never grows/re-centres
            // as results load (results scroll inside).
            'flex h-[min(560px,80vh)] flex-col'
          )}
          onOpenAutoFocus={(e) => {
            // Focus the search input when modal opens
            e.preventDefault();
            const input = document.querySelector(
              '[cmdk-input]'
            ) as HTMLInputElement;
            if (input) {
              setTimeout(() => input.focus(), 0);
            }
          }}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            onOpenChange(false);
          }}
        >
          {/* Close button */}
          <DialogPrimitive.Close className="focus:ring-ring/70 absolute top-4 right-4 z-10 cursor-pointer rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:outline-hidden">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>

          {/* Hidden title for accessibility */}
          <VisuallyHidden>
            <DialogPrimitive.Title>Search</DialogPrimitive.Title>
          </VisuallyHidden>

          <Command
            shouldFilter={false}
            className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                // If search is empty, close modal immediately
                // Otherwise, clear the search first
                if (searchQuery === '') {
                  onOpenChange(false);
                } else {
                  setSearchQuery('');
                }
              }
            }}
          >
            <CommandInput
              placeholder="Search matters or contacts..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />

            {/* Override the shared CommandList's max-h-[410px] so it fills
                the fixed dialog height and scrolls internally instead of
                driving the box's size. */}
            <CommandList className="max-h-none min-h-0 flex-1">
              {!isSearching && (
                <RecentMattersView onSelect={handleSearchResultSelect} />
              )}

              {isSearching && (
                <SearchResultsView
                  query={searchQuery}
                  onSelect={handleSearchResultSelect}
                  onContactSelect={handleContactSelect}
                />
              )}
            </CommandList>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
